import { formatDateRange } from "../format.js";
import { escapeHtml } from "../escape.js";

const styles = `
  /* Not focusable and not clickable itself: the page wraps the card in an
     <a class="card-link"> (see trips-page.js), which owns the focus ring, the
     pointer cursor and the navigation. */
  :host {
    display: block;
    height: 100%;
  }
  /* .trip-grid's grid items stretch to the row height by default (grid's
     align-items: stretch), but that stretch only reaches the grid item - the
     wrapping .card-link, whose own height:100% passes it on to :host - and nothing
     here propagated it into the shadow tree, so two cards in the same row
     ended up different heights whenever one had a .dates line and the
     other didn't. height:100% + this flex column carries the stretched
     height all the way to .body, which is the piece that actually needs
     to absorb the difference. */
  .card {
    height: 100%;
    display: flex;
    flex-direction: column;
    border: 1px solid var(--color-border);
    border-radius: 0.5rem;
    overflow: hidden;
    background: var(--color-surface);
  }
  .card:hover {
    border-color: var(--color-accent);
  }
  .thumb {
    display: block;
    width: 100%;
    height: 8rem;
    object-fit: cover;
  }
  .body {
    flex: 1;
    padding: 1rem;
  }
  /* Explicit size because this is an h2 for document-structure reasons (the
     page's h1 is "Your trips"), not because a card title should be rendered
     at h2 scale. 1.17rem is what the UA gave the h3 this used to be, so the
     card looks exactly as before. */
  h2 {
    margin: 0 0 0.25rem;
    font-size: 1.17rem;
  }
  .dates {
    color: var(--color-text-muted);
    font-size: 0.875rem;
  }
  /* Only rendered for a trip the user doesn't own, so it is the card's way of
     answering "why is this in my list?". Muted and small deliberately: it is
     provenance, not a status the user has to act on. */
  .shared {
    margin-top: 0.25rem;
    color: var(--color-text-muted);
    font-size: 0.8rem;
  }
`;

class TripCard extends HTMLElement {
  static get observedAttributes() {
    return ["trip-id", "title", "start-date", "end-date", "image-url", "shared-label"];
  }

  connectedCallback() {
    if (!this.shadowRoot) this.attachShadow({ mode: "open" });
    this.render();
  }

  attributeChangedCallback() {
    if (this.shadowRoot) this.render();
  }

  render() {
    const title = this.getAttribute("title") || "";
    const imageUrl = this.getAttribute("image-url");
    // Same formatter as the trip header the card links to, so the list and
    // the page it opens don't disagree about how a date looks. It also
    // handles a trip with only one bound set, which the previous inline
    // interpolation rendered as a dangling "2026-08-20 – ".
    const dateRange = formatDateRange(this.getAttribute("start-date"), this.getAttribute("end-date"));
    // Already translated and interpolated by the caller: this element is
    // attribute-driven and has no i18n import, the same way it takes a
    // pre-formatted date range rather than two dates and a formatter.
    const sharedLabel = this.getAttribute("shared-label");

    this.shadowRoot.innerHTML = `
      <style>${styles}</style>
      <div class="card">
        ${imageUrl ? `<img class="thumb" src="${escapeHtml(imageUrl)}" alt="" />` : ""}
        <div class="body">
          <h2>${escapeHtml(title)}</h2>
          ${dateRange ? `<div class="dates">${escapeHtml(dateRange)}</div>` : ""}
          ${sharedLabel ? `<div class="shared">${escapeHtml(sharedLabel)}</div>` : ""}
        </div>
      </div>
    `;
  }
}

customElements.define("trip-card", TripCard);
